/** Variable names whose values must never reach a log sink. */
const REDACTED_VARIABLE_KEYS = ["password", "username", "mfa", "token", "secret"];

const REDACTED = "[redacted]";

/** String.replaceAll on a literal, without regex-escaping the needle. */
const replaceAll = (haystack: string, needle: string, replacement: string): string =>
  haystack.split(needle).join(replacement);

/** Strip secret values out of a GraphQL error before anything logs it.
 *
 * graphql-request's `ClientError` carries the whole request — query *and*
 * variables — and `handleApiError` ships it to Datadog RUM and
 * `console.error` on every FDM failure. Any mutation carrying a secret
 * (`ehrRequestAction` with a LOGIN, `ehrStartWorkflowAndLogin`,
 * `saveEhrCredentials`) therefore logged that secret in plaintext the
 * moment it errored.
 *
 * Two places have to be scrubbed, not one:
 *
 * 1. `err.request.variables`, which the dev-only toast reads directly.
 * 2. `err.message` — `ClientError`'s constructor bakes
 *    `JSON.stringify({ response, request })` into it, so the secret is
 *    still there verbatim even after the variables are cleaned. This is
 *    the copy `datadogRum.addError` and `console.error` actually read,
 *    so scrubbing only the variables left the leak wide open.
 * 3. `err.stack` — V8 builds it as `${name}: ${message}\n    at ...` at
 *    construction, so it carries its own copy of the secret that a later
 *    `message` rewrite does not touch. `datadogRum.addError` reads it.
 *
 * Mutates in place: the same object is what `handleApiError` logs and what
 * callers re-throw, and replacing it would lose the `ClientError` type
 * that downstream `instanceof` checks depend on. Keys are kept so a log
 * still shows which fields were sent, just not their values.
 */
export const redactErrorVariables = (err: unknown): void => {
  const target = err as {
    request?: { variables?: Record<string, unknown> };
    message?: string;
    stack?: string;
  };
  const variables = target?.request?.variables;
  if (!variables) return;

  const secrets: string[] = [];
  for (const key of Object.keys(variables)) {
    if (REDACTED_VARIABLE_KEYS.includes(key.toLowerCase()) && variables[key] != null) {
      const value = variables[key];
      if (typeof value === "string" && value.length > 0) secrets.push(value);
      variables[key] = REDACTED;
    }
  }

  if (secrets.length === 0) return;

  // Longest first. Replacing in key order lets a short secret that is a
  // substring of a longer one (username "admin", password "admin123")
  // consume part of it, after which the longer value no longer matches
  // and its tail stays visible.
  secrets.sort((a, b) => b.length - a.length);

  const scrub = (text: string): string => {
    let scrubbed = text;
    for (const secret of secrets) {
      scrubbed = replaceAll(scrubbed, secret, REDACTED);
      // message/stack hold the JSON-encoded form, so a value containing a
      // quote, backslash or newline appears escaped there and would
      // survive the raw replacement above.
      const encoded = JSON.stringify(secret).slice(1, -1);
      if (encoded !== secret) scrubbed = replaceAll(scrubbed, encoded, REDACTED);
    }
    return scrubbed;
  };

  if (typeof target.message === "string") target.message = scrub(target.message);
  if (typeof target.stack === "string") target.stack = scrub(target.stack);
};
